// Browser-side waveform extraction for audio uploaded straight onto a movie's
// timeline. Library songs are analyzed by the backend waveform worker, but a
// file dropped directly on a channel never enters that collection — so we
// decode it locally (we already hold the File) and reduce it to the same compact
// peaks array the timeline renders. Coarse on purpose: a music lane only needs a
// silhouette, not sample accuracy.
const BUCKETS = 600;

// Reduce a decoded AudioBuffer to `buckets` absolute-peak values in 0..1.
function reducePeaks(audioBuffer, buckets = BUCKETS) {
  const chCount = audioBuffer.numberOfChannels || 1;
  const len = audioBuffer.length;
  if (!len) return [];
  const nb = Math.min(buckets, len);
  const per = Math.max(1, Math.floor(len / nb));
  const peaks = new Array(nb).fill(0);
  // Mix channels by taking the louder sample so stereo content isn't undersold.
  const chans = [];
  for (let c = 0; c < chCount; c += 1) chans.push(audioBuffer.getChannelData(c));
  for (let b = 0; b < nb; b += 1) {
    let mx = 0;
    const start = b * per;
    const end = Math.min(len, start + per);
    for (let i = start; i < end; i += 1) {
      for (let c = 0; c < chans.length; c += 1) {
        const v = Math.abs(chans[c][i]);
        if (v > mx) mx = v;
      }
    }
    peaks[b] = Math.round(mx * 1000) / 1000;
  }
  return peaks;
}

// Decode an audio File (or ArrayBuffer) and return { peaks, durationSec }.
// Resolves to nulls on any failure so callers can fall back to backend analysis.
export async function peaksFromAudioFile(fileOrBuffer, buckets = BUCKETS) {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return { peaks: null, durationSec: 0 };
  let ctx;
  try {
    const arrayBuffer = fileOrBuffer instanceof ArrayBuffer
      ? fileOrBuffer
      : await fileOrBuffer.arrayBuffer();
    ctx = new Ctx();
    // Safari needs the callback form; modern browsers accept the promise form.
    const audioBuffer = await new Promise((resolve, reject) => {
      const p = ctx.decodeAudioData(arrayBuffer, resolve, reject);
      if (p && typeof p.then === 'function') p.then(resolve, reject);
    });
    return {
      peaks: reducePeaks(audioBuffer, buckets),
      durationSec: Math.round((audioBuffer.duration || 0) * 10) / 10,
    };
  } catch {
    return { peaks: null, durationSec: 0 };
  } finally {
    try { if (ctx && ctx.close) ctx.close(); } catch { /* noop */ }
  }
}
